import React, {FormEvent, useState, useEffect} from "react";
import "./PublishForm.css";
import {IBDDef} from "../BD";
import { publishBD, updateBD } from "../../services/editor";
import { IPublishInfo, Post } from "../../services/types";
import {useAuthenticatedAction} from "../../hooks/useAuthenticatedAction";
import { DraftManager } from "./DraftManager";
import Button from "../Button";
import AutoResizeTextarea from "../AutoResizeTextarea";

interface EditMode {
    isEditMode: boolean;
    postId?: number;
    authorId?: number;
    title: string;
    description: string;
    isCertified: boolean;
    isHidden: boolean;
    isVerbi: boolean;
    verbiWord: string;
}

interface PublishFormProps {
    bdDef: IBDDef;
    editMode?: EditMode;
    onPublishSuccess?: (postId: number) => void;
}

const PublishForm : React.FC<PublishFormProps> = ({bdDef, editMode, onPublishSuccess}) => {
    const [publishedPostId, setPublishedPostId] = useState(-1);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState(0);
    const [isCertified, setIsCertified] = useState(false);
    const [isHidden, setIsHidden] = useState(false);
    const [isVerbi, setIsVerbi] = useState(false);
    const [verbiWord, setVerbiWord] = useState("");
    const { execute: createBD, loading: busy, error } = useAuthenticatedAction<{posts: Post[]}, [IPublishInfo]>();
    const { execute: updatePost, loading: updateBusy, error: updateError } = useAuthenticatedAction<{posts: Post[]}, [IPublishInfo]>();

    // Initialize form with edit data if in edit mode
    useEffect(() => {
        if (editMode?.isEditMode) {
            setTitle(editMode.title);
            setDescription(editMode.description);
            setIsCertified(editMode.isCertified);
            setIsHidden(editMode.isHidden);
            setIsVerbi(editMode.isVerbi);
            setVerbiWord(editMode.verbiWord);
            setCategory(editMode.isVerbi ? 7615 : 0);
        }
    }, [editMode]);

    const onPublishFormSubmit = async (e:FormEvent) => {
        e.preventDefault();

        let publishInfo : IPublishInfo = {
            category: category,
            certified: isCertified,
            definition: bdDef,
            description: description,
            hidden: isHidden,
            title: title,
            verbi: isVerbi ? verbiWord : undefined,
            rebounded_bd_id: undefined,
            post_id: editMode?.isEditMode ? editMode.postId : undefined,
            author_id: editMode?.isEditMode ? editMode.authorId : undefined
        }

        let response;
        if (editMode?.isEditMode) {
            // Update existing post
            response = await updatePost(updateBD, publishInfo);
        } else {
            // Create new post
            response = await createBD(publishBD, publishInfo);
        }

        if (response) {
            const publishedPost : Post = response.posts[0];
            setPublishedPostId(publishedPost.ID);
            DraftManager.clearDraft();
            
            // Call the success callback if provided
            if (onPublishSuccess) {
                onPublishSuccess(publishedPost.ID);
            }
        }
        return false;
    }

    const isLoading = editMode?.isEditMode ? updateBusy : busy;
    const currentError = editMode?.isEditMode ? updateError : error;

    return (
        <div className={"PublishForm"}>
            <form onSubmit={onPublishFormSubmit}>
                <h2>{editMode?.isEditMode ? 'Mettre à jour sur BirdsDessinés.fr' : 'Publier sur BirdsDessinés.fr'}</h2>
                <AutoResizeTextarea
                    id="bd-title"
                    name="title"
                    placeholder={"Titre de votre BD*"} 
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)}
                    minRows={1}
                    maxRows={3}
                />
                <AutoResizeTextarea
                    id="bd-description"
                    name="description"
                    value={description} 
                    placeholder={"Courte description"} 
                    onChange={(e) => setDescription(e.target.value)}
                    minRows={2}
                    maxRows={6}
                />
                <label>
                    <input
                        id="is-certified"
                        name="isCertified"
                        type={"checkbox"}
                        checked={isCertified}
                        onChange={(e) => setIsCertified(e.target.checked)}
                    />
                    &nbsp;Blague inédite
                </label>
                <label>
                    <input
                        id="is-hidden"
                        name="isHidden"
                        type={"checkbox"}
                        checked={isHidden}
                        onChange={(e) => setIsHidden(e.target.checked)}
                    />
                    &nbsp;Cacher avec le bandeau (BD offensante, langage cru etc.)
                </label>
                <label>
                    <input
                        id="is-verbi"
                        name="isVerbi"
                        type={"checkbox"}
                        checked={isVerbi}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setIsVerbi(checked);
                            setCategory(checked ? 7615 : 0);
                        }}
                    />
                    &nbsp;Cette BD est une devinette (Verbi)
                </label>
                {
                    isVerbi &&
                    <AutoResizeTextarea
                        id="verbi-word"
                        name="verbiWord"
                        placeholder={"Mot à trouver"}
                        value={verbiWord}
                        onChange={(e) => setVerbiWord(e.target.value)}
                        minRows={1}
                        maxRows={2}
                    />
                }
                <div className={"footer"}>
                    {
                        publishedPostId === -1 &&
                        <Button 
                            type="submit" 
                            variant="primary" 
                            size="medium"
                            disabled={(title === "") || (isVerbi && verbiWord.trim() === "")}
                            loading={isLoading}
                        >
                            {editMode?.isEditMode ? 'Mettre à jour' : 'Publier'}
                        </Button>
                    }
                    {
                        publishedPostId !== -1 &&
                        <div className={"alert alert-success"}>
                            {editMode?.isEditMode ? 'Votre BD a été mise à jour.' : 'Votre BD a été publiée.'}
                        </div>
                    }
                    {
                        currentError !== null &&
                        <div className={"alert alert-error"}>Erreur: {currentError}</div>
                    }
                </div>
            </form>
        </div>
    )
}

export default PublishForm;